import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonIcon,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonItem,
  IonLabel,
  IonAvatar,
  IonText,
  IonButtons,
  IonToast,
  IonTextarea,
  IonSpinner,
  IonGrid,
  IonRow,
  IonCol,
  IonChip
} from '@ionic/react';
import {
  close,
  person,
  mail,
  call,
  business,
  checkmarkCircle,
  copy,
  share,
  time,
  saveOutline,
  documentTextOutline
} from 'ionicons/icons';
import { NetworkingProfile } from '../models/Networking';
import NetworkingService from '../services/NetworkingService';
import './NetworkingContactModal.css';

interface NetworkingContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact: NetworkingProfile | null;
}

const NetworkingContactModal: React.FC<NetworkingContactModalProps> = ({
  isOpen,
  onClose,
  contact
}) => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastColor, setToastColor] = useState<'success' | 'danger'>('success');
  const [isClosing, setIsClosing] = useState(false);
  const [notes, setNotes] = useState('');
  const [savedNotes, setSavedNotes] = useState('');
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  // Resetear estado cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      setShowToast(false);
    }
  }, [isOpen]);

  useEffect(() => {
    let isActive = true;

    const loadNotes = async () => {
      if (!isOpen || !contact) return;

      setNotes('');
      setSavedNotes('');
      setIsLoadingNotes(true);

      try {
        const currentNotes = await NetworkingService.getInstance()
          .getConnectionNotes(contact.id, contact.eventCode);

        if (isActive) {
          setNotes(currentNotes);
          setSavedNotes(currentNotes);
        }
      } catch (error) {
        console.error('NetworkingContactModal: Error al cargar observación:', error);
        if (isActive) {
          setToastMessage('No se pudo cargar la observación.');
          setToastColor('danger');
          setShowToast(true);
        }
      } finally {
        if (isActive) setIsLoadingNotes(false);
      }
    };

    loadNotes();
    return () => {
      isActive = false;
    };
  }, [isOpen, contact]);

  const handleClose = () => {
    if (isClosing) {
      console.log('NetworkingContactModal: Ya se está cerrando, ignorando');
      return;
    }

    console.log('NetworkingContactModal: Cerrando modal');
    setIsClosing(true);

    // Usar setTimeout para evitar conflictos de DOM
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 0);
  };

  const handleDidDismiss = () => {
    console.log('NetworkingContactModal: Modal dismissed');
    // Llamar onClose solo si el modal se cerró por backdrop o gesture
    // pero no si se cerró programáticamente
  };

  // Función para copiar texto al portapapeles
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setToastMessage(`${label} copiado al portapapeles`);
      setShowToast(true);
    } catch (error) {
      console.error('Error al copiar:', error);
      setToastMessage('Error al copiar');
      setShowToast(true);
    }
  };

  // Función para compartir contacto
  const shareContact = async () => {
    if (!contact) return;

    const contactInfo = `
Contacto de Networking:
Nombre: ${contact.name}
${contact.company ? `Empresa: ${contact.company}` : ''}
${contact.position ? `Cargo: ${contact.position}` : ''}
${contact.email ? `Email: ${contact.email}` : ''}
${contact.phone ? `Teléfono: ${contact.phone}` : ''}
    `.trim();

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Contacto: ${contact.name}`,
          text: contactInfo
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback: copiar al portapapeles
      copyToClipboard(contactInfo, 'Información del contacto');
    }
  };

  const saveNotes = async () => {
    if (!contact || isSavingNotes) return;

    try {
      setIsSavingNotes(true);
      await NetworkingService.getInstance()
        .updateConnectionNotes(contact.id, contact.eventCode, notes);

      const normalizedNotes = notes.trim();
      setNotes(normalizedNotes);
      setSavedNotes(normalizedNotes);
      setToastMessage(normalizedNotes ? 'Observación guardada.' : 'Observación eliminada.');
      setToastColor('success');
      setShowToast(true);
    } catch (error) {
      console.error('NetworkingContactModal: Error al guardar observación:', error);
      setToastMessage(error instanceof Error ? error.message : 'No se pudo guardar la observación.');
      setToastColor('danger');
      setShowToast(true);
    } finally {
      setIsSavingNotes(false);
    }
  };

  // Función para obtener iniciales
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // No renderizar el modal si no hay contacto
  if (!contact) {
    return null;
  }

  return (
    <IonModal
      key={contact?.id || 'networking-modal'}
      isOpen={isOpen}
      onDidDismiss={handleDidDismiss}
      backdropDismiss={true}
      className="networking-contact-modal"
    >
      <IonHeader>
        <IonToolbar>
          <IonTitle>Contacto Agregado</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleClose}>
              <IonIcon icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <IonIcon
            icon={checkmarkCircle}
            className="success-icon"
            style={{
              fontSize: '4rem',
              color: 'var(--ion-color-success)',
              marginBottom: '10px'
            }}
          />
          <h2 style={{ color: 'var(--ion-color-success)', margin: '0', fontWeight: '600' }}>
            ¡Contacto Agregado!
          </h2>
        </div>

        <IonCard>
          <IonCardHeader>
            <IonCardTitle style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <IonAvatar style={{ width: '70px', height: '70px' }}>
                <div style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'var(--ion-color-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  borderRadius: '50%'
                }}>
                  {getInitials(contact.name)}
                </div>
              </IonAvatar>
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: '0', fontSize: '1.4rem', fontWeight: '600' }}>
                  {contact.name}
                </h2>
                {contact.position && (
                  <IonChip color="primary" style={{ margin: '4px 0' }}>
                    <IonIcon icon={person} />
                    <IonLabel>{contact.position}</IonLabel>
                  </IonChip>
                )}
                {contact.company && (
                  <p style={{ margin: '4px 0 0 0', color: 'var(--ion-color-medium)', fontSize: '1rem' }}>
                    <IonIcon icon={business} style={{ marginRight: '4px' }} />
                    {contact.company}
                  </p>
                )}
              </div>
            </IonCardTitle>
          </IonCardHeader>

          <IonCardContent>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ margin: '0 0 12px 0', color: 'var(--ion-color-dark)' }}>
                Información de Contacto
              </h3>
            </div>

            {contact.email && (
              <IonItem lines="none" style={{ '--padding-start': '0', marginBottom: '8px' }}>
                <IonIcon icon={mail} slot="start" color="primary" />
                <IonLabel>
                  <h3>Email</h3>
                  <p style={{ fontSize: '1rem', fontWeight: '500' }}>{contact.email}</p>
                </IonLabel>
                <IonButton
                  fill="clear"
                  slot="end"
                  className="copy-button"
                  onClick={() => copyToClipboard(contact.email!, 'Email')}
                >
                  <IonIcon icon={copy} />
                </IonButton>
              </IonItem>
            )}

            {contact.phone && (
              <IonItem lines="none" style={{ '--padding-start': '0', marginBottom: '8px' }}>
                <IonIcon icon={call} slot="start" color="primary" />
                <IonLabel>
                  <h3>Teléfono</h3>
                  <p style={{ fontSize: '1rem', fontWeight: '500' }}>{contact.phone}</p>
                </IonLabel>
                <IonButton
                  fill="clear"
                  slot="end"
                  className="copy-button"
                  onClick={() => copyToClipboard(contact.phone!, 'Teléfono')}
                >
                  <IonIcon icon={copy} />
                </IonButton>
              </IonItem>
            )}

            {/* Información adicional si está disponible */}
            {(contact.company || contact.position) && (
              <div style={{ marginTop: '20px' }}>
                <h3 style={{ margin: '0 0 12px 0', color: 'var(--ion-color-dark)' }}>
                  Información Profesional
                </h3>

                {contact.company && (
                  <IonItem lines="none" style={{ '--padding-start': '0', marginBottom: '8px' }}>
                    <IonIcon icon={business} slot="start" color="secondary" />
                    <IonLabel>
                      <h3>Empresa</h3>
                      <p style={{ fontSize: '1rem', fontWeight: '500' }}>{contact.company}</p>
                    </IonLabel>
                  </IonItem>
                )}

                {contact.position && (
                  <IonItem lines="none" style={{ '--padding-start': '0', marginBottom: '8px' }}>
                    <IonIcon icon={person} slot="start" color="secondary" />
                    <IonLabel>
                      <h3>Cargo</h3>
                      <p style={{ fontSize: '1rem', fontWeight: '500' }}>{contact.position}</p>
                    </IonLabel>
                  </IonItem>
                )}
              </div>
            )}
          </IonCardContent>
        </IonCard>

        <IonCard className="networking-notes-card">
          <IonCardHeader>
            <IonCardTitle className="networking-notes-title">
              <IonIcon icon={documentTextOutline} />
              Mis observaciones
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <p className="networking-notes-help">
              Guarda un dato que te ayude a recordar este contacto. Esta observación solo pertenece a tu conexión.
            </p>

            {isLoadingNotes ? (
              <div className="networking-notes-loading">
                <IonSpinner name="crescent" />
                <span>Cargando observación...</span>
              </div>
            ) : (
              <>
                <IonTextarea
                  className="networking-notes-input"
                  value={notes}
                  maxlength={1000}
                  rows={4}
                  autoGrow={true}
                  placeholder="Ej.: conversar sobre la propuesta, llamar la próxima semana..."
                  onIonChange={(event) => setNotes(event.detail.value || '')}
                />
                <div className="networking-notes-counter">{notes.length}/1000</div>
                <IonButton
                  expand="block"
                  onClick={saveNotes}
                  disabled={isSavingNotes || notes.trim() === savedNotes}
                  className="networking-notes-save"
                >
                  {isSavingNotes ? (
                    <IonSpinner name="crescent" />
                  ) : (
                    <IonIcon icon={saveOutline} slot="start" />
                  )}
                  {isSavingNotes ? 'Guardando...' : 'Guardar observación'}
                </IonButton>
              </>
            )}
          </IonCardContent>
        </IonCard>

        {/* Botones de acción */}
        <div style={{ marginTop: '24px' }}>
          <IonGrid>
            <IonRow>
              <IonCol size="6">
                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={shareContact}
                  style={{ '--border-radius': '12px' }}
                >
                  <IonIcon icon={share} slot="start" />
                  Compartir
                </IonButton>
              </IonCol>
              <IonCol size="6">
                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={() => copyToClipboard(
                    `${contact.name}\n${contact.email || ''}\n${contact.phone || ''}`,
                    'Información del contacto'
                  )}
                  style={{ '--border-radius': '12px' }}
                >
                  <IonIcon icon={copy} slot="start" />
                  Copiar Todo
                </IonButton>
              </IonCol>
            </IonRow>
          </IonGrid>
        </div>

        <div style={{ marginTop: '16px' }}>
          <IonText color="medium">
            <p style={{ textAlign: 'center', fontSize: '0.9rem' }}>
              <IonIcon icon={time} style={{ marginRight: '4px' }} />
              Este contacto ha sido guardado en tu lista de networking.
            </p>
          </IonText>
        </div>

        <IonButton
          expand="block"
          onClick={handleClose}
          style={{
            marginTop: '20px',
            '--background': 'var(--ion-color-primary)',
            '--border-radius': '12px',
            '--box-shadow': '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}
        >
          Continuar
        </IonButton>

        {/* Toast para notificaciones */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={2000}
          position="bottom"
          color={toastColor}
        />
      </IonContent>
    </IonModal>
  );
};

export default NetworkingContactModal;
